import { useState, useMemo, useRef } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'
import { ALL_LANGUAGES, DEFAULT_TOP_LANGUAGE_CODES } from '../../lib/constants/languages'

interface LanguageSelectorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

export function LanguageSelector({ value, onChange, disabled }: LanguageSelectorProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [inputActive, setInputActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Get selected language details
  const selectedLanguage = ALL_LANGUAGES.find((lang) => lang.code === value)

  // Filter and organize languages
  const { topLanguages, otherLanguages } = useMemo(() => {
    const searchLower = search.toLowerCase()
    const filtered = ALL_LANGUAGES.filter(
      (lang) =>
        lang.name.toLowerCase().includes(searchLower) ||
        lang.code.toLowerCase().includes(searchLower)
    )

    const top = filtered.filter((lang) => DEFAULT_TOP_LANGUAGE_CODES.includes(lang.code))
      .sort((a, b) => {
        // Sort by order in DEFAULT_TOP_LANGUAGE_CODES
        const indexA = DEFAULT_TOP_LANGUAGE_CODES.indexOf(a.code)
        const indexB = DEFAULT_TOP_LANGUAGE_CODES.indexOf(b.code)
        return indexA - indexB
      })
    const other = filtered.filter((lang) => !DEFAULT_TOP_LANGUAGE_CODES.includes(lang.code))
      .sort((a, b) => {
        // Sort alphabetically by name
        return a.name.localeCompare(b.name)
      })

    return { topLanguages: top, otherLanguages: other }
  }, [search])

  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen)
      if (!isOpen) {
        setInputActive(false)
      }
    }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="language-selector-btn w-full justify-between font-normal"
        >
          {selectedLanguage ? (
            <span className="flex items-center gap-2">
              <span>{selectedLanguage.flag}</span>
              <span>{selectedLanguage.name}</span>
              <span className="text-muted-foreground">({selectedLanguage.code})</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{t('common.selectLanguagePlaceholder')}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="language-selector-popover w-[400px] p-0" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t('common.searchLanguages')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              readOnly={!inputActive}
              onTouchStart={() => {
                setInputActive(true)
                // Small delay to ensure readOnly is removed before focusing
                setTimeout(() => inputRef.current?.focus(), 0)
              }}
              onClick={() => {
                if (!inputActive) {
                  setInputActive(true)
                }
              }}
            />
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {topLanguages.length > 0 && (
            <div>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {t('common.commonLanguages')}
              </div>
              {topLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    onChange(language.code)
                    setOpen(false)
                    setSearch('')
                  }}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                    value === language.code && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === language.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="mr-2">{language.flag}</span>
                  <span className="flex-1 text-left">{language.name}</span>
                  <span className="text-xs text-muted-foreground">{language.code}</span>
                </button>
              ))}
            </div>
          )}
          {otherLanguages.length > 0 && (
            <div className={topLanguages.length > 0 ? 'mt-2 border-t pt-2' : ''}>
              {topLanguages.length > 0 && (
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {t('common.otherLanguages')}
                </div>
              )}
              {otherLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    onChange(language.code)
                    setOpen(false)
                    setSearch('')
                  }}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                    value === language.code && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === language.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="mr-2">{language.flag}</span>
                  <span className="flex-1 text-left">{language.name}</span>
                  <span className="text-xs text-muted-foreground">{language.code}</span>
                </button>
              ))}
            </div>
          )}
          {topLanguages.length === 0 && otherLanguages.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t('common.noLanguagesFound')}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

